import { inject, Injectable } from '@angular/core';
import { Appointment, CreateAppointmentParams, UpdateAppointmentParams } from '../models/appointment.model';
import { AuthService } from './auth.service';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService, type QuerySignal } from './database.service';

/**
 * Appointment booking records (design doc §6.4) — a handoff record, not a
 * scheduling engine. Booking itself goes through a SchedulingProvider (see
 * core/services/scheduling/); this service is the plain CRUD underneath it.
 */
@Injectable({
  providedIn: 'root',
})
export class AppointmentService {
  private db = inject(DatabaseService);
  private clockSync = inject(ClockSyncService);
  private authService = inject(AuthService);

  async getById(id: string): Promise<Appointment | null> {
    await this.db.init();
    const rows = await this.db.exec('SELECT * FROM appointments WHERE id = ? AND deleted_at IS NULL', [id]);
    return rows.length > 0 ? (rows[0] as Appointment) : null;
  }

  async getAllForLead(leadId: string): Promise<Appointment[]> {
    await this.db.init();
    const rows = await this.db.exec(
      'SELECT * FROM appointments WHERE lead_id = ? AND deleted_at IS NULL ORDER BY scheduled_start',
      [leadId],
    );
    return rows as Appointment[];
  }

  createQueryForLead(leadId: string): QuerySignal<Appointment[]> {
    return this.db.createQuery('appointments', () => this.getAllForLead(leadId));
  }

  /** A user's still-actionable appointments scheduled before `before`, soonest first — for My Day. */
  async getUpcomingForUser(userId: string, before: number): Promise<Appointment[]> {
    await this.db.init();
    const rows = await this.db.exec(
      `SELECT * FROM appointments
       WHERE assigned_to = ? AND status IN ('scheduled', 'confirmed') AND scheduled_start < ? AND deleted_at IS NULL
       ORDER BY scheduled_start ASC`,
      [userId, before],
    );
    return rows as Appointment[];
  }

  createQueryUpcomingForUser(userId: string, before: number): QuerySignal<Appointment[]> {
    return this.db.createQuery('appointments', () => this.getUpcomingForUser(userId, before));
  }

  async create(params: CreateAppointmentParams): Promise<Appointment> {
    const userId = this.requireUser('book');
    await this.db.init();
    const id = this.db.generateId();
    const now = this.clockSync.now();
    const timezone = params.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

    await this.db.run(
      `INSERT INTO appointments (
         id, lead_id, address_id, appointment_type, scheduled_start, scheduled_end, timezone,
         assigned_to, notes, created_by, created_at, updated_by, updated_at
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        params.lead_id,
        params.address_id,
        params.appointment_type ?? 'estimate',
        params.scheduled_start,
        params.scheduled_end,
        timezone,
        params.assigned_to ?? userId,
        params.notes ?? '',
        userId,
        now,
        userId,
        now,
      ],
    );

    const created = await this.getById(id);
    if (!created) throw new Error('Failed to retrieve appointment after creation');
    return created;
  }

  async update(params: UpdateAppointmentParams): Promise<Appointment> {
    const userId = this.requireUser('update');
    await this.db.init();
    const existing = await this.getById(params.id);
    if (!existing) throw new Error(`Appointment '${params.id}' not found`);

    const next: Appointment = {
      ...existing,
      appointment_type: params.appointment_type ?? existing.appointment_type,
      scheduled_start: params.scheduled_start ?? existing.scheduled_start,
      scheduled_end: params.scheduled_end ?? existing.scheduled_end,
      assigned_to: params.assigned_to ?? existing.assigned_to,
      status: params.status ?? existing.status,
      notes: params.notes ?? existing.notes,
    };

    await this.db.run(
      `UPDATE appointments SET appointment_type = ?, scheduled_start = ?, scheduled_end = ?, assigned_to = ?,
         status = ?, notes = ?, updated_by = ?, updated_at = ?
       WHERE id = ?`,
      [
        next.appointment_type,
        next.scheduled_start,
        next.scheduled_end,
        next.assigned_to,
        next.status,
        next.notes,
        userId,
        this.clockSync.now(),
        params.id,
      ],
    );

    return { ...next, updated_by: userId };
  }

  private requireUser(action: string): string {
    const userId = this.authService.getCurrentUserId();
    if (!userId) throw new Error(`User must be authenticated to ${action} an appointment`);
    return userId;
  }
}
